import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';

import { matchSorter, MatchSorterOptions } from 'match-sorter';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import Icon from '@/components/base/Icon';
// Components
import Input from '@/components/base/Input';
// Types
import type { OptionType, SelectProps } from '@/components/base/Select';
import Select from '@/components/base/Select';
import DefaultOption from '@/components/base/Select/DefaultOption';

type SelectFilterProps = SelectProps & {
    sortOptions?: MatchSorterOptions<OptionType>;
    idInput?: string;
    children?: ReactElement;
    onSearch?: (searchValue: string) => void;
    footer?: ReactElement;
};

/**
 * @deprecated Use SelectFilterComponent instead
 */
const SelectFilter = ({
    options = [],
    sortOptions = { keys: [{ threshold: matchSorter.rankings.CONTAINS, key: 'label' }] },
    idInput,
    children,
    onSearch,
    footer,
    ...restProps
}: SelectFilterProps) => {
    const { t } = useTranslation('commons');
    const [optionsData, setOptionsData] = useState(options ?? []);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        if (filter !== '') {
            setOptionsData(matchSorter(options, filter, sortOptions));
        } else {
            setOptionsData(options);
        }
    }, [filter, options]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.code === 'Space') {
            e.stopPropagation();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);

        if (onSearch) {
            onSearch(e.target.value);
        }
    };

    const handleInput = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (onSearch && e.currentTarget.value !== filter) {
            onSearch(e.currentTarget.value);
        }
    };

    return (
        <Select hasFilter={true} options={null} {...restProps}>
            <div className="sticky left-0 top-0 z-20 w-full bg-white pt-1 dark:bg-icoDarkMode-anthracite">
                <Input
                    beforeInput={<Icon icon="search" />}
                    inputStyle="search_select"
                    id={idInput}
                    className="mb-1"
                    noBorder={true}
                    value={filter}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    onInput={handleInput}
                />
            </div>
            {!optionsData || (optionsData && !optionsData.length) ? (
                <div className="flex justify-center p-1">{t('select_no_options')}</div>
            ) : null}
            {optionsData?.map((option, id) => (
                <Select.Option
                    key={
                        typeof option?.value === 'string' || typeof option?.value === 'number'
                            ? option.value
                            : id
                    }
                    disabled={option?.disabled ?? null}
                    value={option}>
                    {({ selected, active, disabled }) => (
                        <>
                            {children ? (
                                React.Children.map(children, (child) => {
                                    const newChild = React.cloneElement(child, {
                                        selected,
                                        disabled,
                                        active,
                                        option
                                    } as React.Attributes);
                                    return newChild;
                                })
                            ) : (
                                <DefaultOption
                                    selected={selected}
                                    disabled={disabled}
                                    option={option}
                                />
                            )}
                        </>
                    )}
                </Select.Option>
            ))}
            {footer}
        </Select>
    );
};

export default SelectFilter;
